import { createSelector } from "@reduxjs/toolkit";
import abacCache from "utils/abacCache";

/**
 * Base selector to get ABAC state
 */
const selectAbacState = (state) => state.abac;

/**
 * Select user context
 */
export const selectUserContext = createSelector(
  [selectAbacState],
  (abac) => abac.userContext
);

/**
 * Select user capabilities
 */
export const selectCapabilities = createSelector(
  [selectAbacState],
  (abac) => abac.capabilities
);

/**
 * Select capabilities loading state
 */
export const selectCapabilitiesLoading = createSelector(
  [selectAbacState],
  (abac) => abac.loading.capabilities
);

/**
 * Select capabilities error
 */
export const selectCapabilitiesError = createSelector(
  [selectAbacState],
  (abac) => abac.error.capabilities
);

/**
 * Select all permitted actions from capabilities
 * Returns flat list like ["asset.view", "asset.create", "workorder.view"]
 */
export const selectCapabilityActions = createSelector(
  [selectCapabilities],
  (capabilities) => capabilities?.actions || []
);

/**
 * Select actions grouped by resource type
 * Returns object like { Asset: ["View", "Create"], WorkOrder: ["View"] }
 */
export const selectActionsByResourceType = createSelector(
  [selectCapabilities],
  (capabilities) => capabilities?.actions_by_resource_type || {}
);

/**
 * Select feature flags from capabilities
 */
export const selectFeatureFlags = createSelector(
  [selectCapabilities],
  (capabilities) => capabilities?.flags || {}
);

/**
 * Check if a capability is present
 * @param {string} resourceType - Type of resource (e.g., "Asset" or "asset")
 * @param {string} action - Action (e.g., "View" or "view")
 */
export const selectHasCapability = (resourceType, action) =>
  createSelector([selectCapabilityActions], (actions) => {
    if (!Array.isArray(actions)) return false;
    // Normalize to lowercase for comparison
    const normalizedCheck = `${resourceType.toLowerCase()}.${action.toLowerCase()}`;
    return actions.some((a) => a.toLowerCase() === normalizedCheck);
  });

/**
 * Check if a feature flag is enabled
 * @param {string} flagName - Name of the feature flag
 */
export const selectHasFeatureFlag = (flagName) =>
  createSelector([selectFeatureFlags], (flags) => flags[flagName] === true);

/**
 * Select user roles from context
 */
export const selectUserRoles = createSelector(
  [selectUserContext],
  (userContext) => userContext?.roles || []
);

/**
 * Select user attributes from context
 */
export const selectUserAttributes = createSelector(
  [selectUserContext],
  (userContext) => userContext?.attributes || {}
);

/**
 * Select loading state for user context
 */
export const selectUserContextLoading = createSelector(
  [selectAbacState],
  (abac) => abac.loading.userContext
);

/**
 * Select permission cache
 */
export const selectPermissionCache = createSelector(
  [selectAbacState],
  (abac) => abac.permissionCache
);

/**
 * Check if a specific permission is granted
 * @param {string} resourceType - Type of resource
 * @param {string} action - Action to perform
 * @param {string} [resourceId] - Optional specific resource ID
 */
export const selectIsPermitted = (resourceType, action, resourceId = null) =>
  createSelector([selectPermissionCache], (cache) => {
    const cacheKey = abacCache.constructor.generateKey(
      resourceType,
      action,
      resourceId
    );

    const cached = cache[cacheKey];

    if (!cached) {
      return null; // Not evaluated yet
    }

    // Check if cache entry is still valid (5 minute TTL)
    const now = Date.now();
    const ttl = 5 * 60 * 1000;

    if (now - cached.timestamp > ttl) {
      return null; // Cache expired
    }

    return cached.granted === true;
  });

/**
 * Select all permitted actions for a resource type
 * @param {string} resourceType - Type of resource
 */
export const selectPermittedActions = (resourceType) =>
  createSelector([selectAbacState], (abac) => {
    const cached = abac.permittedActions[resourceType];

    if (!cached) {
      return null; // Not loaded yet
    }

    // Check if cache entry is still valid (5 minute TTL)
    const now = Date.now();
    const ttl = 5 * 60 * 1000;

    if (now - cached.timestamp > ttl) {
      return null; // Cache expired
    }

    return cached.actions;
  });

/**
 * Select loading state for permitted actions
 * @param {string} resourceType - Type of resource
 */
export const selectPermittedActionsLoading = (resourceType) =>
  createSelector(
    [selectAbacState],
    (abac) => abac.loading.permittedActions[resourceType] || false
  );

/**
 * Select hierarchy data
 */
export const selectHierarchy = createSelector(
  [selectAbacState],
  (abac) => abac.hierarchy
);

/**
 * Select loading state for hierarchy
 */
export const selectHierarchyLoading = createSelector(
  [selectAbacState],
  (abac) => abac.loading.hierarchy
);

/**
 * Select metrics
 */
export const selectMetrics = createSelector(
  [selectAbacState],
  (abac) => abac.metrics
);

/**
 * Calculate cache hit rate
 */
export const selectCacheHitRate = createSelector([selectMetrics], (metrics) => {
  const total = metrics.cacheHits + metrics.cacheMisses;
  if (total === 0) {
    return 0;
  }
  return ((metrics.cacheHits / total) * 100).toFixed(2);
});

/**
 * Select error state for user context
 */
export const selectUserContextError = createSelector(
  [selectAbacState],
  (abac) => abac.error.userContext
);

/**
 * Select error state for permission evaluation
 */
export const selectPermissionError = createSelector(
  [selectAbacState],
  (abac) => abac.error.permission
);

/**
 * Select error state for batch evaluation
 */
export const selectBatchError = createSelector(
  [selectAbacState],
  (abac) => abac.error.batch
);

/**
 * Select error state for permitted actions
 * @param {string} resourceType - Type of resource
 */
export const selectPermittedActionsError = (resourceType) =>
  createSelector(
    [selectAbacState],
    (abac) => abac.error.permittedActions[resourceType] || null
  );

/**
 * Check if user has a specific role
 * @param {string} roleName - Name of the role
 */
export const selectHasRole = (roleName) =>
  createSelector([selectUserRoles], (roles) => {
    if (!Array.isArray(roles)) {
      return false;
    }
    return roles.some(
      (role) =>
        role === roleName ||
        role?.name === roleName ||
        role?.roleName === roleName
    );
  });

/**
 * Check if user has any of the specified roles
 * @param {Array<string>} roleNames - Array of role names
 */
export const selectHasAnyRole = (roleNames) =>
  createSelector([selectUserRoles], (roles) => {
    if (!Array.isArray(roles) || !Array.isArray(roleNames)) {
      return false;
    }

    return roleNames.some((roleName) =>
      roles.some(
        (role) =>
          role === roleName ||
          role?.name === roleName ||
          role?.roleName === roleName
      )
    );
  });

/**
 * Check if user has all of the specified roles
 * @param {Array<string>} roleNames - Array of role names
 */
export const selectHasAllRoles = (roleNames) =>
  createSelector([selectUserRoles], (roles) => {
    if (!Array.isArray(roles) || !Array.isArray(roleNames)) {
      return false;
    }

    return roleNames.every((roleName) =>
      roles.some(
        (role) =>
          role === roleName ||
          role?.name === roleName ||
          role?.roleName === roleName
      )
    );
  });

/**
 * Get user attribute value
 * @param {string} attributeName - Name of the attribute
 */
export const selectUserAttribute = (attributeName) =>
  createSelector(
    [selectUserAttributes],
    (attributes) => attributes[attributeName] || null
  );

/**
 * Select loading state for permission evaluation
 */
export const selectPermissionLoading = createSelector(
  [selectAbacState],
  (abac) => abac.loading.permission
);

/**
 * Select loading state for batch evaluation
 */
export const selectBatchLoading = createSelector(
  [selectAbacState],
  (abac) => abac.loading.batch
);
